import type {
  PlacardColor,
  QuantityDiscountTier,
  SizePreset,
  SizeTier,
  VinylColor,
} from './types.js';

export const SIZE_TIERS: readonly SizeTier[] = [
  { id: 'tier1', displayName: 'Up to 1" × 5"',  maxLong: 5,        maxShort: 1,        basePrice:  7.5 },
  { id: 'tier2', displayName: 'Up to 2" × 6"',  maxLong: 6,        maxShort: 2,        basePrice:  9.5 },
  { id: 'tier3', displayName: 'Up to 4" × 11"', maxLong: 11,       maxShort: 4,        basePrice: 12.5 },
  { id: 'tier4', displayName: 'Over 4" × 11"',  maxLong: Infinity, maxShort: Infinity, basePrice: 16.5 },
] as const;

// Presets are stored with width = long (horizontal) side and height = short (vertical) side.
// ID patches are always horizontal on a vest; the spec's "1" × 4"" label convention
// is "short × long", handled by formatSizeLabel() at display time.
export const SIZE_PRESETS: readonly SizePreset[] = [
  { width: 4,  height: 1 },
  { width: 5,  height: 1 },
  { width: 4,  height: 2 },
  { width: 5,  height: 2 },
  { width: 6,  height: 2 },
  { width: 8,  height: 3 },
  { width: 10, height: 3 },
  { width: 11, height: 3 },
  { width: 8,  height: 4 },
  { width: 11, height: 4 },
] as const;

export const CUSTOM_SIZE = {
  minInches: 1,
  maxInches: 12,
  incrementInches: 0.25,
  surchargePerPanel: 3.0,
  waiverMinQuantity: 10,
} as const;

export const REFLECTIVE_SURCHARGE_PER_PANEL = 2.0;

export const VINYL_COLORS: readonly VinylColor[] = [
  { id: 'tactical-black',        displayName: 'Tactical Black',        technicalName: 'Black',                 hex: '#1a1a1a', reflective: false },
  { id: 'sheriff-gold',          displayName: 'Sheriff Gold',          technicalName: 'Gold',                  hex: '#c9a227', reflective: false },
  { id: 'duty-white',            displayName: 'Duty White',            technicalName: 'White',                 hex: '#f5f5f5', reflective: false },
  { id: 'granite-gray',          displayName: 'Granite Gray',          technicalName: 'Gray',                  hex: '#707070', reflective: false },
  { id: 'patrol-silver',         displayName: 'Patrol Silver',         technicalName: '3M Reflective Silver',  hex: '#c0c0c0', reflective: true  },
  { id: 'vegas-gold',            displayName: 'Vegas Gold',            technicalName: 'Vegas Gold',            hex: '#b8860b', reflective: false },
  { id: 'reflective-vegas-gold', displayName: 'Reflective Vegas Gold', technicalName: 'Reflective Vegas Gold', hex: '#d4a84a', reflective: true  },
  { id: 'shadow-gray',           displayName: 'Shadow Gray',           technicalName: 'Dark Gray',             hex: '#3a3a3a', reflective: false },
] as const;

export const PLACARD_COLORS: readonly PlacardColor[] = [
  { id: 'tactical-black',  displayName: 'Tactical Black',  technicalName: 'Black',              hex: '#1a1a1a', isPattern: false },
  { id: 'ranger-olive',    displayName: 'Ranger Olive',    technicalName: 'Olive',              hex: '#4b5320', isPattern: false },
  { id: 'patrol-navy',     displayName: 'Patrol Navy',     technicalName: 'Navy',               hex: '#152238', isPattern: false },
  { id: 'multicam',        displayName: 'Multicam',        technicalName: 'Multicam',           hex: '#7a6a4a', isPattern: true  },
  { id: 'sheriff-brown',   displayName: 'Sheriff Brown',   technicalName: 'Sheriff Brown',      hex: '#5a3825', isPattern: false },
  { id: 'desert-tan',      displayName: 'Desert Tan',      technicalName: 'Tan',                hex: '#c2b280', isPattern: false },
  { id: 'coyote',          displayName: 'Coyote',          technicalName: 'Coyote',             hex: '#81613c', isPattern: false },
  { id: 'safety-yellow',   displayName: 'Safety Yellow',   technicalName: 'Fluorescent Yellow', hex: '#f3e600', isPattern: false },
] as const;

export const FONTS = [
  { id: 'helvetica-black', displayName: 'Helvetica Black', webFallback: 'Inter Black' },
] as const;

export const TEXT_RULES = {
  minLines: 1,
  maxLines: 3,
  maxCharsPerLine: 20,
  smallHeightPct: 0.22,
  largeHeightPct: 0.45,
  paddingPct: 0.08,
  allowedChars: /^[A-Z0-9 .,\-'/#&]*$/,
} as const;

export const HEM_STITCH = {
  // Inset from the placard edge, in inches. Universal ⅛" across all sizes.
  inset: 0.125,
} as const;

export const DEFAULT_QUANTITY_DISCOUNT_TIERS: readonly QuantityDiscountTier[] = [
  { minQty: 1,  maxQty: 9,    discountPercent: 0 },
  { minQty: 10, maxQty: 24,   discountPercent: 0 },
  { minQty: 25, maxQty: 49,   discountPercent: 0 },
  { minQty: 50, maxQty: null, discountPercent: 0 },
] as const;
